import { FaGoogle } from '@react-icons/all-files/fa/FaGoogle'
import { FaMicrosoft } from '@react-icons/all-files/fa/FaMicrosoft'
import { RiSettings3Line } from '@react-icons/all-files/ri/RiSettings3Line'
import type { FC, FormEvent } from 'react'
import { ReactNode, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Button } from '~/components/common/Button'
import { EmailInput } from '~/components/common/EmailInput'
import { Link } from '~/components/common/Link'
import PasswordInput from '~/components/common/PasswordInput'
import SignInMfaVerify from '~/components/auth/SigninMfaVerify'
import { SiteLayoutV2 } from '~/components/layout/SiteLayoutV2'
import { IconCrossFilled } from '~/components/icons'
import { OktaIcon } from '~/components/icons/sso'

import { useAppConfig } from '~/contexts/AppConfigContext'
import { usePlatform } from '~/contexts/PlatformContext'
import { useUserContext } from '~/contexts/UserContext'
import { pluginAuthService } from '~/services/plugin/authService'
import {
  checkDomainConfiguration,
  extractDomainFromEmail,
  getSSOLoginUrl,
  isPublicEmailDomain
} from '~/services/tenantService'
import { signin } from '~/services/userService'
import { openExternal } from '~/utils/openExternal'
import { BetterToast } from '~/utils/toast'

const Page: FC = () => {
  const navigate = useNavigate()
  const { config } = useAppConfig()
  const { isPlugin } = usePlatform()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [mfaCode, setMfaCode] = useState<string | null>(null)
  const [showMfaPrompt, setShowMfaPrompt] = useState(false)
  const [isDisabled, setIsDisabled] = useState(false)
  const [errorMessage, setErrorMessage] = useState<ReactNode>('')
  const [isEmailValid, setIsEmailValid] = useState(true)
  const { refetch } = useUserContext()

  // SSO-related state
  const [ssoAvailable, setSsoAvailable] = useState<boolean | null>(null)
  const [ssoConfig, setSsoConfig] = useState<{
    provider_type?: string
    domain?: string
    hosted_ui_url?: string
  } | null>(null)
  const [checkingSSO, setCheckingSSO] = useState(false)

  useEffect(() => {
    if (isPlugin) return

    const url = new URL(window.location.href)

    const error = url.searchParams.get('error')
    const errorType = url.searchParams.get('error_type')
    const logoutUrl = url.searchParams.get('logout_url') || '/logout'
    if (error && errorType === 'sso') {
      if (error === 'account_conflict') {
        setErrorMessage(
          'Account conflict: This email is already associated with another SSO account. Please contact support or try logging in with a different method.'
        )
      } else if (error === 'email_mismatch') {
        setErrorMessage(
          <span>
            Email mismatch: The email provided by your SSO provider does not match your account
            email. Please{' '}
            <a href={logoutUrl} className='underline text-blue-600'>
              go to your SSO provider
            </a>{' '}
            to sign out and try again with the correct account.
          </span>
        )
      } else {
        setErrorMessage(
          'Unable to sign in with SSO. Please try again or contact your administrator if the problem persists.'
        )
      }
      url.searchParams.delete('error')
      url.searchParams.delete('error_type')
      window.history.replaceState({}, '', url.toString())
    }
  }, [isPlugin])

  // Check for SSO configuration when email changes
  useEffect(() => {
    setSsoAvailable(null)
    setSsoConfig(null)

    if (!email || !email.includes('@')) {
      return
    }

    const emailParts = email.split('@')
    if (emailParts.length !== 2 || emailParts[0].length === 0 || emailParts[1].length === 0) {
      return
    }

    const domain = extractDomainFromEmail(email)
    if (isPublicEmailDomain(domain)) {
      return
    }

    setCheckingSSO(true)

    const timeoutId = setTimeout(async () => {
      try {
        const result = await checkDomainConfiguration(domain)

        if (result.configured && result.status === 'active') {
          setSsoAvailable(true)
          setSsoConfig({
            provider_type: result.provider_type,
            domain: result.domain,
            hosted_ui_url: result.hosted_ui_url
          })
        } else if (result.configured && result.status === 'pending') {
          setSsoAvailable(false)
          setSsoConfig({ domain: result.domain })
        } else {
          setSsoAvailable(false)
          setSsoConfig({ domain })
        }
      } catch (error: unknown) {
        if (isPlugin) {
          setSsoAvailable(true)
          setSsoConfig({ domain })
        } else {
          const msg = error instanceof Error ? error.message : 'Unknown error'
          BetterToast.error(msg || 'Failed to check SSO configuration')
          setSsoAvailable(false)
          setSsoConfig(null)
        }
      } finally {
        setCheckingSSO(false)
      }
    }, 500)

    return () => {
      clearTimeout(timeoutId)
      setCheckingSSO(false)
    }
  }, [email, isPlugin])

  const handleMfaCodeChange = (code: string) => {
    setMfaCode(code)
  }

  const handleCancelMfaVerify = () => {
    setShowMfaPrompt(false)
    setMfaCode(null)
  }

  const handleSSOLogin = async () => {
    if (!email) {
      BetterToast.error('Please enter your email address first')
      return
    }

    try {
      setIsDisabled(true)

      if (isPlugin) {
        const result = await pluginAuthService.startSSOAuthWithEmail(email)
        if (!result.success) {
          BetterToast.error(result.error || 'SSO login failed')
        }
      } else {
        const result = await getSSOLoginUrl(email)
        if (result.success && result.login_url) {
          openExternal(result.login_url)
        } else {
          BetterToast.error(result.error || 'Failed to get SSO login URL')
        }
      }
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Unknown error'
      BetterToast.error(msg || 'Failed to initiate SSO login')
    } finally {
      setIsDisabled(false)
    }
  }

  const handleSetupSSO = () => {
    if (!email) {
      BetterToast.error('Please enter your email address first')
      return
    }

    const domain = extractDomainFromEmail(email)
    navigate(`/sso-setup?domain=${encodeURIComponent(domain)}&email=${encodeURIComponent(email)}`)
  }

  const handleSignIn = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setErrorMessage('')

    try {
      setIsDisabled(true)

      if (isPlugin) {
        const result = await pluginAuthService.loginWithPassword(
          email,
          password,
          mfaCode ?? undefined
        )
        if (result.mfaRequired) {
          setShowMfaPrompt(true)
          setIsDisabled(false)
          return
        }
        if (!result.success) {
          setErrorMessage(result.error || 'Login failed')
          setIsDisabled(false)
          return
        }
        await refetch()
        navigate('/', { replace: true })
      } else {
        const response = await signin(email, password, mfaCode)
        await refetch()

        if ('mfa_required' in response) {
          setShowMfaPrompt(true)
          setIsDisabled(false)
        } else {
          const { referer } = response
          const path = referer || '/'
          return navigate(path, { replace: true })
        }
      }
    } catch (err) {
      const error = err as unknown as Error
      setErrorMessage(error.message)
      if (error.message === 'Wrong MFA code') {
        BetterToast.error('Wrong MFA code')
      }
      setIsDisabled(false)
    }
  }

  const getSSOButtonIcon = () => {
    if (!ssoConfig?.provider_type) return <FaMicrosoft />

    switch (ssoConfig.provider_type) {
      case 'microsoft':
        return <FaMicrosoft />
      case 'google':
        return <FaGoogle />
      case 'okta':
        return <OktaIcon />
      default:
        return <FaMicrosoft />
    }
  }

  const getSSOButtonText = () => {
    if (!ssoConfig?.provider_type) return 'Continue with SSO'

    switch (ssoConfig.provider_type) {
      case 'microsoft':
        return 'Continue with Microsoft'
      case 'google':
        return 'Continue with Google'
      case 'okta':
        return 'Continue with Okta'
      case 'saml':
        return 'Continue with SAML'
      default:
        return 'Continue with SSO'
    }
  }

  const mfaForm = showMfaPrompt && (
    <SignInMfaVerify
      email={email}
      password={password}
      mfaCode={mfaCode ?? ''}
      handleMfaCodeChange={handleMfaCodeChange}
      handleCancelMfaVerify={handleCancelMfaVerify}
    />
  )

  const loginForm = !showMfaPrompt && (
    <>
      <form id='loginForm' onSubmit={handleSignIn}>
        <div className='mb-4'>
          <EmailInput
            id='email'
            placeholder='Email'
            value={email}
            onChange={e => setEmail(e.target.value)}
            onValidationChange={setIsEmailValid}
            autoComplete='email'
            autoFocus
            className='text-base'
            height='lg'
          />
        </div>

        <div>
          <PasswordInput
            id='password'
            type={'password'}
            placeholder='Password'
            value={password}
            onChange={e => setPassword(e.target.value)}
            autoComplete='current-password'
            height='lg'
          />
        </div>

        <div className='flex items-center text-red-600 text-[14px] mb-8 my-[2px] px-1 min-h-8 py-1 gap-1'>
          {errorMessage && (
            <>
              <IconCrossFilled className='mr-1 mt-0.5 shrink-0' />
              <div className='select-none'>{errorMessage}</div>
            </>
          )}
        </div>

        <div className='w-full flex justify-center mb-6 flex-col items-center'>
          <Button
            variant='primary'
            id='signin-button'
            type='submit'
            isDisabled={isDisabled || !email || !password || !isEmailValid}
            className='w-full px-10 py-3 text-center leading-[22px] h-[46px] mb-2'
          >
            Log In
          </Button>
          {config.email_verification_enabled && (
            <div className='flex justify-center'>
              <Link href='/reset-password'>
                <span className='text-tokens-neutral-text-primary text-[13px] select-none'>
                  Forgot Password
                </span>
              </Link>
            </div>
          )}
        </div>

        {/* SSO Section */}
        {email && ssoAvailable !== null && (
          <>
            <div className='relative mb-6'>
              <div className='absolute inset-0 flex items-center'>
                <div className='w-full border-t border-gray-300' />
              </div>
              <div className='relative flex justify-center text-sm'>
                <span className='px-2 bg-tokens-neutral-bg-white text-tokens-neutral-text-primary select-none'>
                  OR
                </span>
              </div>
            </div>

            {ssoAvailable === true && (
              <Button
                type='button'
                variant='outlined'
                isDisabled={isDisabled || checkingSSO}
                onClick={handleSSOLogin}
                className='w-full flex items-center justify-center space-x-2 h-[46px]'
              >
                {getSSOButtonIcon()}
                <span>{getSSOButtonText()}</span>
              </Button>
            )}

            {ssoAvailable === false && (
              <Button
                type='button'
                variant='outlined'
                isDisabled={isDisabled || checkingSSO}
                onClick={handleSetupSSO}
                className='w-full flex items-center justify-center space-x-2 h-[46px]'
              >
                <RiSettings3Line />
                <span>Configure SSO</span>
              </Button>
            )}
          </>
        )}
      </form>

      {/* Legacy SSO Button */}
      {config.sso_enabled && !email && (
        <Button
          variant='outlined'
          isDisabled={isDisabled}
          onClick={() => {
            navigate(`/sso/${config.sso_type}/login`)
          }}
          className='mt-4 w-full flex items-center justify-center space-x-2'
        >
          <FaMicrosoft />
          <span>Sign in with Microsoft</span>
        </Button>
      )}
    </>
  )

  return (
    <SiteLayoutV2>
      <div className='w-[440px] max-w-[calc(100vw-32px)] flex justify-center items-start h-auto bg-tokens-neutral-bg-white rounded-3xl'>
        <div className='w-full px-10 py-12'>
          {showMfaPrompt ? (
            mfaForm
          ) : (
            <>
              <div>
                <h1
                  className='mb-2 text-center font-bold text-3xl select-none'
                  aria-label='Login title'
                >
                  Log In
                </h1>
                <div className='mb-10 flex justify-center'>
                  <span className='text-lg font-medium select-none'>Have an account?</span>{' '}
                  <Link href='/signup'>
                    <span className='font-bold text-tokens-neutral-text-primary select-none'>
                      Sign Up
                    </span>
                  </Link>
                </div>
              </div>
              {loginForm}
            </>
          )}
        </div>
      </div>
    </SiteLayoutV2>
  )
}

export default Page
